import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IntegrationsMeliPublishingService,
  IntegrationsPortalPublicationsService,
  MeliEnqueuedPublicationDraftResponse,
  type MeliPublicationDraftPersistResponse,
  type MeliPublicationDraftResponse,
  type PrepareMeliPublicationDraftParams,
} from 'src/api/generated';
import { type ApiResponse } from 'src/hooks/api/api';

interface UsePrepareMeliPublicationDraftResponse {
  prepareDraft: (
    params: PrepareMeliPublicationDraftParams
  ) => Promise<MeliPublicationDraftResponse>;
  loading: boolean;
  error: Error | null;
}

interface UseMeliPublicationDraftPreviewResponse extends ApiResponse {
  draft: MeliPublicationDraftResponse | null;
}

export const useMeliPublicationDraftPreview = (
  params: PrepareMeliPublicationDraftParams | null,
  enabled = true
): UseMeliPublicationDraftPreviewResponse => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery<MeliPublicationDraftResponse | null>({
    queryKey: ['integrations', 'meli', 'draft', 'preview', params],
    queryFn: async () => {
      if (!params) {
        return null;
      }

      return IntegrationsMeliPublishingService.preparePublicationDraft(params);
    },
    enabled: enabled && params != null,
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  return {
    loading,
    error,
    refetch,
    draft: data ?? null,
  };
};

export const usePrepareMeliPublicationDraft =
  (): UsePrepareMeliPublicationDraftResponse => {
    const { mutateAsync, isPending, error } = useMutation({
      mutationKey: ['integrations', 'meli', 'draft', 'prepare'],
      mutationFn: async (
        params: PrepareMeliPublicationDraftParams
      ): Promise<MeliPublicationDraftResponse> =>
        await IntegrationsMeliPublishingService.preparePublicationDraft(params),
    });

    return {
      prepareDraft: mutateAsync,
      loading: isPending,
      error: error instanceof Error ? error : null,
    };
  };

interface UseEnqueueMeliPublishResponse {
  enqueuePublish: (
    params: PrepareMeliPublicationDraftParams
  ) => Promise<MeliEnqueuedPublicationDraftResponse>;
  loading: boolean;
  error: Error | null;
}

export const useEnqueueMeliPublish = (): UseEnqueueMeliPublishResponse => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, error } = useMutation({
    mutationKey: ['integrations', 'meli', 'publish', 'enqueue'],
    mutationFn: async (
      params: PrepareMeliPublicationDraftParams
    ): Promise<MeliEnqueuedPublicationDraftResponse> => {
      const persisted: MeliPublicationDraftPersistResponse =
        await IntegrationsMeliPublishingService.prepareAndPersistPublicationDraft(
          params
        );
      const queued =
        await IntegrationsPortalPublicationsService.enqueuePublicationPublish(
          persisted.publication.id
        );

      return {
        publication: queued.publication,
        draft: persisted.draft,
        action: MeliEnqueuedPublicationDraftResponse.action.PUBLISH,
        queued: queued.queued,
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['integrations', 'portal-publications'],
      });
    },
  });

  return {
    enqueuePublish: mutateAsync,
    loading: isPending,
    error: error instanceof Error ? error : null,
  };
};
